import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { TrendsAggregationLevel, TrendsPropertyType } from '@root/search/models/map.constants';
import { select, Store } from '@ngrx/store';
import { AppState } from '@root/app-state.module';
import {
  selectSelectedTrendsLayer,
  selectSelectedTrendsLayers,
  selectTrendsAggregationLevel,
  selectTrendsAvailableLayers,
  selectTrendsOpacity,
  selectTrendsPropertyType
} from '@store/map-layers.selector';
import { Observable } from 'rxjs';
import { SetTrendsAggregationLevel, SetTrendsLayer, SetTrendsOpacity, SetTrendsPropertyType } from '@store/search-board/search-board.actions';
import { LayerInfo } from '@root/search/models/map.models';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { selectSidePanelState } from '@store/search-board/search-board.selector';
import { SidePanelTab, SidePanelView } from '@store/search-board/search-board.model';
import { MixpanelService } from '@shared/services/mixpanel.service';
import { SearchMixpanelUtil } from '@root/search/search-mixpanel-util';

@UntilDestroy()
@Component({
  selector: 'rlst-map-trends',
  templateUrl: './map-trends.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapTrendsComponent implements OnInit {
  @Input() index: number;

  public opacityLevelForm: UntypedFormGroup;
  public readonly trendsAggregationLevels: Array<{ label: string, value: TrendsAggregationLevel }> = [
    {label: 'Zip Code', value: TrendsAggregationLevel.ZIP},
    {label: 'Neighborhood', value: TrendsAggregationLevel.NEIGHBORHOOD}
  ];
  public readonly trendsPropertyTypes: Array<{ label: string, value: TrendsPropertyType }> = [
    {label: 'SFR', value: TrendsPropertyType.SFR},
    {label: 'Condo', value: TrendsPropertyType.CONDO},
    {label: 'Both', value: TrendsPropertyType.BOTH}
  ];
  public trendLayers$: Observable<Array<LayerInfo>>;
  public selectedTrendLayer$: Observable<LayerInfo>;
  public selectedAggregationLevel$: Observable<TrendsAggregationLevel>;
  public selectedPropertyType$: Observable<TrendsPropertyType>;
  public selectedOpacity$: Observable<number>;


  private sidePanelTabOpened: SidePanelTab;
  private readonly searchMixpanelUtil: SearchMixpanelUtil = new SearchMixpanelUtil(this.store$);

  constructor(
    private readonly store$: Store<AppState>,
    private readonly fb: UntypedFormBuilder,
    private readonly mixpanelService: MixpanelService
  ) {
  }

  public ngOnInit(): void {
    this.trendLayers$ = this.store$.pipe(select(selectTrendsAvailableLayers));
    this.selectedTrendLayer$ = this.store$.pipe(
      select(selectSelectedTrendsLayer),
    );
    this.selectedAggregationLevel$ = this.store$.pipe(select(selectTrendsAggregationLevel));
    this.selectedPropertyType$ = this.store$.pipe(select(selectTrendsPropertyType));
    const HALF_TRANSPARENT = 50;
    this.opacityLevelForm = this.fb.group({
      opacityLevel: [HALF_TRANSPARENT]
    });
    this.store$.pipe(select(selectTrendsOpacity), untilDestroyed(this)).subscribe((opacity: number) => {
      this.opacityLevelForm.setValue({
        opacityLevel: opacity
      }, {emitEvent: false});
    });

    this.opacityLevelForm.get('opacityLevel')
      .valueChanges
      .pipe(
        untilDestroyed(this),
      )
      .subscribe((opacityLevel) => this.setOpacity(opacityLevel));

    this.store$
      .pipe(
        select(selectSidePanelState)
      )
      .subscribe((sidePanelView: SidePanelView) => {
        this.sidePanelTabOpened = sidePanelView.sidePanelTabOpened;
      });
  }

  public setAggregationLevel(aggregationLevel: TrendsAggregationLevel): void {
    this.store$.dispatch(new SetTrendsAggregationLevel(aggregationLevel));
  }

  public setPropertyType(propertyType: TrendsPropertyType): void {
    this.store$.dispatch(new SetTrendsPropertyType(propertyType));
  }

  public setTrendsLayer(layer: LayerInfo): void {
    if (layer.layerId !== 'select_none') {
      this.logMapTrendsMixPanel('Search - Trend Added', layer.label);
    } else {
      this.store$.select(selectSelectedTrendsLayers).forEach((layersInfo) => {
        if (layersInfo != null) {
          layersInfo.forEach((layerInfo) => {
            this.logMapTrendsMixPanel('Search - Trend Removed', layerInfo.label);
          });
        } else {
          this.logMapTrendsMixPanel('Search - Trend Removed', 'Unavailable');
        }
      });
    }

    this.store$.dispatch(new SetTrendsLayer(layer));
  }

  private setOpacity(opacityLevel: number): void {
    this.store$.dispatch(new SetTrendsOpacity(opacityLevel));
  }

  trackByFn(_: any, item: LayerInfo) {
    return item.layerId;
  }

  private logMapTrendsMixPanel(eventId: string, trendLayer: string) {
    this.searchMixpanelUtil.setupMixPanelItems(this.sidePanelTabOpened);

    this.mixpanelService.track(eventId, {
      countyList: this.searchMixpanelUtil.countyList,
      searchType: this.sidePanelTabOpened,
      isSavedSearch: (this.sidePanelTabOpened === 'quickSearch') ? false :
        this.searchMixpanelUtil.selectedSearch !== 'My Search Default',
      searchParameters: this.searchMixpanelUtil.searchFieldNames.join('; '),
      trendLayer: trendLayer,
      numberOfResults: this.searchMixpanelUtil.totalSearchRecords
    });
  }
}
